import { adjustAllFragments } from "./adjustAllFragments";
import { markManualOverlaps } from "./manualOverlaps";
import { AudioTrack, AudioTrackData } from "./types";

/**
 * Как колонка озвучки рисует места дублей (prepareData провайдера), реплики уже по месту.
 *
 * - «Без автоправок» — места как в файлах, наезды помечены (`markManualOverlaps`).
 * - Живой эфир — тоже места как в файлах, без раскладки при показе. Раскладку папки эфира делают проход папки и круг
 *   правки на сервере (и называют её в отчёте правки), а раскладка при показе (`adjustAllFragments`, зазор 50 мс)
 *   рисовала соседей не там, где лежат их дубли. Проба 20261105-P50m20h, папка 1, E6: ♪2 удлинил дубль реплики
 *   «Ivan, what might help?» 1745 → 1750 мс (30820 + 1750 = 32570), и соседка «No.» на ленте в тот же миг встала на 5 мс
 *   позже — 32615 → 32620 (32570 + 50), хотя на сервере её никто не двигал: журнал правок папки молчит, склейка
 *   прохода в 11:50:30 сдвигала её с 32614,99. Переводчик видел сдвиг соседки от своего ♪, которого не было (п.50г).
 *   Наезд, если он есть в файлах, так и виден: его снимет проход или круг правки, а не рисунок.
 * - Иначе — прежняя раскладка: наехавшая или стоящая ближе 50 мс озвучка рисуется позже (TTS бывает длиннее оригинала).
 *
 * Возвращает новую колонку (или ту же, если двигать нечего); реплики без wav остаются в ней.
 */
export function layoutVoicingColumn(
    tracks: AudioTrack[],
    { manualOnly = false, liveStream = false }: { manualOnly?: boolean; liveStream?: boolean } = {},
): AudioTrack[] {
    if (manualOnly) {
        return markManualOverlaps(tracks);
    }
    if (liveStream) {
        return tracks;
    }
    // adjustAllFragments returns EVERY cue, including the ones with no wav yet — this assignment replaces the whole
    // column, and a cue missing from it is a cue the translator can no longer see is missing its voiceover.
    if (tracks.some((t) => !!t.file)) {
        const { shifted } = adjustAllFragments(tracks as unknown as AudioTrackData[]);
        if (shifted.length > 0) {
            return shifted as unknown as AudioTrack[];
        }
    }
    return tracks;
}
